export interface CourseModule {
    id: number;
    title: string;
    topics: string[];
    day?: string;
    hasMiniMock?: boolean;
    hasAIDrills?: boolean;
    hasAudioPractice?: boolean;
}

export interface CourseSection {
    id: number;
    title: string;
    dayRange: string;
    icon: string;
    color: string;
    modules: CourseModule[];
}

export const IELTS_UKVI_COURSE: CourseSection[] = [
    {
        id: 1,
        title: 'Orientation & Foundation',
        dayRange: 'Day 1',
        icon: '🧭',
        color: '#009B8F',
        modules: [
            {
                id: 1,
                title: 'Welcome & System Setup',
                topics: [
                    'How the IELTS UKVI exam works',
                    'Band score system explained',
                    'Test format (Listening, Reading, Writing, Speaking)',
                    'How to use the platform',
                    'Microphone test + audio visualiser',
                    'Student dashboard walkthrough',
                    'Day 1 Quick Start Audio Practice',
                ],
                day: 'Day 1',
                hasAudioPractice: true,
            },
            {
                id: 2,
                title: 'Initial Skills Assessment',
                topics: [
                    '20-minute diagnostic test',
                    'AI-generated band prediction',
                    'Strengths & weaknesses analysis',
                    'Recommended study path',
                    'Day 1 Mini Mock (Speaking + Writing)',
                ],
                day: 'Day 1',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 2,
        title: 'Speaking Mastery',
        dayRange: 'Days 2–10',
        icon: '🎙️',
        color: '#1DE9B6',
        modules: [
            {
                id: 3,
                title: 'Speaking Part 1 – Introduction & Interview',
                topics: [
                    'Fluency training',
                    'Natural tone practice',
                    'Common question bank',
                    'Daily Audio Drills',
                ],
                day: 'Days 2–3',
                hasMiniMock: true,
                hasAudioPractice: true,
            },
            {
                id: 4,
                title: 'Speaking Part 2 – Cue Card Mastery',
                topics: [
                    '3-step cue card structure',
                    'Idea-generation technique',
                    '50 cue card topics',
                    'Cue Card Audio Prompts',
                ],
                day: 'Days 4–5',
                hasMiniMock: true,
                hasAudioPractice: true,
            },
            {
                id: 5,
                title: 'Speaking Part 3 – Discussion Questions',
                topics: [
                    'Opinion development',
                    'Linking phrases',
                    'High-band vocabulary',
                    'Discussion Audio Trainer',
                ],
                day: 'Days 6–8',
                hasAudioPractice: true,
            },
            {
                id: 6,
                title: 'Full Speaking Simulation',
                topics: [
                    'Part 1 + Part 2 + Part 3 combined',
                    'AI scoring & feedback',
                ],
                day: 'Day 9–10',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 3,
        title: 'Writing Mastery',
        dayRange: 'Days 11–15',
        icon: '✍️',
        color: '#ffa726',
        modules: [
            {
                id: 7,
                title: 'Writing Task 1 (Academic & General)',
                topics: [
                    'Academic: charts, graphs, maps',
                    'General: letter writing',
                    'Templates for each type',
                    '20 practice tasks',
                    'Timed Writing Drills',
                ],
                day: 'Days 11–13',
                hasAIDrills: true,
            },
            {
                id: 8,
                title: 'Writing Task 2 – Essay Writing',
                topics: [
                    '4 essay templates',
                    'Band 9 structure',
                    'Linking words & cohesion',
                    '10 practice essays',
                    'AI scoring & feedback',
                ],
                day: 'Days 14–15',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 4,
        title: 'Reading Mastery',
        dayRange: 'Days 16–20',
        icon: '📖',
        color: '#42a5f5',
        modules: [
            {
                id: 9,
                title: 'Reading Strategies',
                topics: [
                    'Skimming & scanning techniques',
                    'Keyword matching',
                    'Time management',
                ],
                day: 'Days 16–17',
            },
            {
                id: 10,
                title: 'Reading Question Types',
                topics: [
                    'True / False / Not Given',
                    'Matching headings',
                    'Sentence completion',
                    'Multiple choice',
                    'Diagram completion',
                    'Daily Reading Drills',
                ],
                day: 'Days 17–18',
                hasAIDrills: true,
            },
            {
                id: 11,
                title: 'Academic vs General Reading',
                topics: [
                    'Academic: long passages',
                    'General: short functional texts',
                ],
                day: 'Day 19',
            },
            {
                id: 12,
                title: 'Mini Mock: Reading',
                topics: [
                    'Full timed reading section',
                    'AI scoring & breakdown',
                ],
                day: 'Day 20',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 5,
        title: 'Listening Mastery',
        dayRange: 'Days 21–25',
        icon: '🎧',
        color: '#ab47bc',
        modules: [
            {
                id: 13,
                title: 'Listening Skills',
                topics: [
                    'Accent training',
                    'Prediction technique',
                    'Note-taking strategies',
                ],
                day: 'Days 21–22',
                hasAudioPractice: true,
            },
            {
                id: 14,
                title: 'Listening Question Types',
                topics: [
                    'Form completion',
                    'Map / diagram labelling',
                    'Multiple choice',
                    'Matching',
                    'Sentence completion',
                ],
                day: 'Days 22–23',
                hasAIDrills: true,
            },
            {
                id: 15,
                title: 'Audio Practice Library',
                topics: [
                    '20 practice audios',
                    'Daily Listening Summaries',
                ],
                day: 'Day 24',
                hasAudioPractice: true,
            },
            {
                id: 16,
                title: 'Mini Mock: Listening',
                topics: [
                    'Full timed listening section',
                    'AI scoring & breakdown',
                ],
                day: 'Day 25',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 6,
        title: 'Mock Tests & AI Feedback',
        dayRange: 'Days 26–30',
        icon: '🏆',
        color: '#ef5350',
        modules: [
            {
                id: 17,
                title: 'Full Mock Tests',
                topics: [
                    '3 full-length IELTS UKVI mock tests',
                    'Auto-timed testing',
                    'Auto-scored results',
                    'Speaking AI analysis',
                    'Writing AI scoring',
                    'Listening & reading breakdown',
                ],
                day: 'Days 26–28',
                hasMiniMock: true,
                hasAIDrills: true,
            },
            {
                id: 18,
                title: 'Performance Dashboard',
                topics: [
                    'Band prediction',
                    'Weak area identification',
                    'Improvement tracker',
                    'Study recommendations',
                ],
                day: 'Days 29–30',
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 7,
        title: 'Bonus Tools',
        dayRange: 'Premium Only',
        icon: '💎',
        color: '#26c6da',
        modules: [
            {
                id: 19,
                title: 'Vocabulary Builder',
                topics: [
                    '500 IELTS words',
                    'Audio pronunciation',
                    'Flashcards',
                    'Mini quizzes',
                ],
                hasAudioPractice: true,
            },
            {
                id: 20,
                title: 'Grammar Booster',
                topics: [
                    '20 grammar micro-lessons',
                    'Error correction practice',
                ],
                hasAIDrills: true,
            },
            {
                id: 21,
                title: 'Speaking Fluency Trainer',
                topics: [
                    'Shadowing exercises',
                    'Accent neutralisation',
                    'Speed control drills',
                ],
                hasAudioPractice: true,
            },
            {
                id: 22,
                title: 'Writing Templates Library',
                topics: [
                    'Task 1 templates',
                    'Task 2 templates',
                    'Connectors list',
                ],
            },
        ],
    },
    {
        id: 8,
        title: 'Student Support',
        dayRange: 'Ongoing',
        icon: '🤝',
        color: '#66bb6a',
        modules: [
            {
                id: 23,
                title: 'WhatsApp Support (Optional Add-On)',
                topics: [
                    'Ask questions anytime',
                    'Get expert feedback',
                    'Motivation support',
                ],
            },
            {
                id: 24,
                title: 'Live Classes (Optional Add-On)',
                topics: [
                    'Weekly live sessions',
                    'Q&A with tutors',
                    'Live speaking practice',
                ],
            },
        ],
    },
];

export const TOTAL_MODULES = 24;
export const TOTAL_DAYS = 30;

// ── IELTS Academic 30-Day Fast-Track Course ──

export const IELTS_ACADEMIC_COURSE: CourseSection[] = [
    {
        id: 1,
        title: 'Orientation & Foundation',
        dayRange: 'Day 1',
        icon: '🧭',
        color: '#009B8F',
        modules: [
            {
                id: 1,
                title: 'Welcome & System Setup',
                topics: [
                    'How the IELTS Academic exam works',
                    'Band score system explained',
                    'Test format (Listening, Reading, Writing, Speaking)',
                    'How to use the platform',
                    'Microphone test + audio visualiser',
                    'Student dashboard walkthrough',
                    'Day 1 Quick Start Audio Practice',
                ],
                day: 'Day 1',
                hasAudioPractice: true,
            },
            {
                id: 2,
                title: 'Initial Skills Assessment',
                topics: [
                    '20-minute diagnostic test',
                    'AI-generated band prediction',
                    'Strengths & weaknesses',
                    'Recommended study path',
                    'Day 1 Mini Mock (Speaking + Writing)',
                ],
                day: 'Day 1',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 2,
        title: 'Speaking Mastery',
        dayRange: 'Days 2–10',
        icon: '🎙️',
        color: '#1DE9B6',
        modules: [
            {
                id: 3,
                title: 'Speaking Part 1 – Introduction & Interview',
                topics: [
                    'Fluency training',
                    'Natural tone practice',
                    'Common question bank',
                    'Daily Audio Drills',
                    'Mini Mock: Speaking Part 1 (Day 3)',
                ],
                day: 'Days 2–3',
                hasMiniMock: true,
                hasAudioPractice: true,
            },
            {
                id: 4,
                title: 'Speaking Part 2 – Cue Card Mastery',
                topics: [
                    '3-step cue card structure',
                    'Idea-generation technique',
                    '50 cue card topics',
                    'Cue Card Audio Prompts',
                    'Mini Mock: Cue Card (Day 5)',
                ],
                day: 'Days 4–5',
                hasMiniMock: true,
                hasAudioPractice: true,
            },
            {
                id: 5,
                title: 'Speaking Part 3 – Discussion Questions',
                topics: [
                    'Opinion development',
                    'Linking phrases',
                    'High-band vocabulary',
                    'Discussion Audio Trainer',
                ],
                day: 'Days 6–8',
                hasAudioPractice: true,
            },
            {
                id: 6,
                title: 'Full Speaking Simulation',
                topics: [
                    'Part 1 + Part 2 + Part 3',
                    'AI scoring',
                    'Mini Mock: Full Speaking Test (Day 10)',
                ],
                day: 'Day 9–10',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 3,
        title: 'Writing Mastery',
        dayRange: 'Days 11–15',
        icon: '✍️',
        color: '#ffa726',
        modules: [
            {
                id: 7,
                title: 'Writing Task 1 (Academic Only)',
                topics: [
                    'Describing charts, graphs, tables, maps, processes',
                    'Band 9 structure',
                    'Templates for each type',
                    '20 practice tasks',
                    'Timed Writing Drills',
                ],
                day: 'Days 11–13',
                hasAIDrills: true,
            },
            {
                id: 8,
                title: 'Writing Task 2 – Essay Writing',
                topics: [
                    '4 essay templates',
                    'Band 9 structure',
                    'Linking words',
                    '10 practice essays',
                    'AI scoring',
                    'Mini Mock: Writing (Day 15)',
                ],
                day: 'Days 14–15',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 4,
        title: 'Reading Mastery',
        dayRange: 'Days 16–20',
        icon: '📖',
        color: '#42a5f5',
        modules: [
            {
                id: 9,
                title: 'Academic Reading Strategies',
                topics: [
                    'Skimming & scanning',
                    'Keyword matching',
                    'Time management',
                ],
                day: 'Days 16–17',
            },
            {
                id: 10,
                title: 'Academic Question Types',
                topics: [
                    'True/False/Not Given',
                    'Matching headings',
                    'Sentence completion',
                    'Multiple choice',
                    'Diagram completion',
                    'Daily Reading Drills',
                ],
                day: 'Days 17–18',
                hasAIDrills: true,
            },
            {
                id: 11,
                title: 'Academic Passage Mastery',
                topics: [
                    'Long-form academic texts',
                    'Complex vocabulary',
                    'Paraphrasing skills',
                ],
                day: 'Day 19',
            },
            {
                id: 12,
                title: 'Mini Mock: Reading',
                topics: [
                    'Full timed section',
                    'AI scoring',
                ],
                day: 'Day 20',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 5,
        title: 'Listening Mastery',
        dayRange: 'Days 21–25',
        icon: '🎧',
        color: '#ab47bc',
        modules: [
            {
                id: 13,
                title: 'Listening Skills',
                topics: [
                    'Accent training',
                    'Prediction technique',
                    'Note-taking',
                ],
                day: 'Days 21–22',
                hasAudioPractice: true,
            },
            {
                id: 14,
                title: 'Listening Question Types',
                topics: [
                    'Form completion',
                    'Map/diagram',
                    'Multiple choice',
                    'Matching',
                    'Sentence completion',
                ],
                day: 'Days 22–23',
                hasAIDrills: true,
            },
            {
                id: 15,
                title: 'Audio Practice Library',
                topics: [
                    '20 practice audios',
                    'Daily Listening Summaries',
                ],
                day: 'Day 24',
                hasAudioPractice: true,
            },
            {
                id: 16,
                title: 'Mini Mock: Listening',
                topics: [
                    'Full timed section',
                    'AI scoring',
                ],
                day: 'Day 25',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 6,
        title: 'Mock Tests & AI Feedback',
        dayRange: 'Days 26–30',
        icon: '🏆',
        color: '#ef5350',
        modules: [
            {
                id: 17,
                title: 'Full Mock Tests',
                topics: [
                    '3 full-length IELTS Academic mock tests',
                    'Auto-timed',
                    'Auto-scored',
                    'Speaking AI analysis',
                    'Writing AI scoring',
                    'Listening & reading breakdown',
                ],
                day: 'Days 26–28',
                hasMiniMock: true,
                hasAIDrills: true,
            },
            {
                id: 18,
                title: 'Performance Dashboard',
                topics: [
                    'Band prediction',
                    'Weak area identification',
                    'Improvement tracker',
                    'Study recommendations',
                ],
                day: 'Days 29–30',
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 7,
        title: 'Bonus Tools',
        dayRange: 'Premium Only',
        icon: '💎',
        color: '#26c6da',
        modules: [
            {
                id: 19,
                title: 'Vocabulary Builder',
                topics: [
                    '500 IELTS Academic words',
                    'Audio pronunciation',
                    'Flashcards',
                    'Mini quizzes',
                ],
                hasAudioPractice: true,
            },
            {
                id: 20,
                title: 'Grammar Booster',
                topics: [
                    '20 grammar micro-lessons',
                    'Error correction practice',
                ],
                hasAIDrills: true,
            },
            {
                id: 21,
                title: 'Speaking Fluency Trainer',
                topics: [
                    'Shadowing',
                    'Accent neutralisation',
                    'Speed control',
                ],
                hasAudioPractice: true,
            },
            {
                id: 22,
                title: 'Writing Templates Library',
                topics: [
                    'Task 1 Academic templates',
                    'Task 2 templates',
                    'Connectors list',
                ],
            },
        ],
    },
    {
        id: 8,
        title: 'Student Support',
        dayRange: 'Ongoing',
        icon: '🤝',
        color: '#66bb6a',
        modules: [
            {
                id: 23,
                title: 'WhatsApp Support (Optional Add-On)',
                topics: [
                    'Ask questions',
                    'Get feedback',
                    'Motivation support',
                ],
            },
            {
                id: 24,
                title: 'Live Classes (Optional Add-On)',
                topics: [
                    'Weekly live sessions',
                    'Q&A',
                    'Speaking practice',
                ],
            },
        ],
    },
];

export const ACADEMIC_TOTAL_MODULES = 24;
export const ACADEMIC_TOTAL_DAYS = 30;

// ── PTE Academic 30-Day Fast-Track Course ──

export const PTE_ACADEMIC_COURSE: CourseSection[] = [
    {
        id: 1,
        title: 'Orientation & Foundation',
        dayRange: 'Day 1',
        icon: '🧭',
        color: '#009B8F',
        modules: [
            {
                id: 1,
                title: 'Welcome & System Setup',
                topics: [
                    'How the PTE Academic exam works',
                    'How scoring works',
                    'How to use the platform',
                    'Microphone test + audio visualiser',
                    'Student dashboard walkthrough',
                    'Day 1 Quick Start Audio Practice',
                ],
                day: 'Day 1',
                hasAudioPractice: true,
            },
            {
                id: 2,
                title: 'Initial Skills Assessment',
                topics: [
                    '20-minute diagnostic test',
                    'AI-generated skill report',
                    'Strengths & weaknesses',
                    'Recommended study path',
                    'Day 1 Mini Mock (Speaking + Writing)',
                ],
                day: 'Day 1',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 2,
        title: 'Speaking Mastery',
        dayRange: 'Days 2–10',
        icon: '🎙️',
        color: '#1DE9B6',
        modules: [
            {
                id: 3,
                title: 'Read Aloud',
                topics: [
                    'Strategy videos',
                    'Pronunciation training',
                    'Pace control exercises',
                    'AI scoring + instant feedback',
                    '30 practice items',
                    'Daily Audio Drills',
                    'Mini Mock: Read Aloud (Day 3)',
                ],
                day: 'Days 2–3',
                hasMiniMock: true,
                hasAudioPractice: true,
                hasAIDrills: true,
            },
            {
                id: 4,
                title: 'Repeat Sentence',
                topics: [
                    'Memory training',
                    'Chunking technique',
                    'Shadowing exercises',
                    'Audio visualiser',
                    '50 practice items',
                    'Audio Repetition Trainer',
                    'Mini Mock: Repeat Sentence (Day 5)',
                ],
                day: 'Days 4–5',
                hasMiniMock: true,
                hasAudioPractice: true,
            },
            {
                id: 5,
                title: 'Describe Image',
                topics: [
                    'Templates for each image type',
                    'Structure training',
                    'AI speech analysis',
                    '20 practice items',
                    'Describe Image Audio Prompts',
                ],
                day: 'Days 6–7',
                hasAudioPractice: true,
                hasAIDrills: true,
            },
            {
                id: 6,
                title: 'Re-tell Lecture',
                topics: [
                    'Note-taking method',
                    'Structure template',
                    'Audio + video lectures',
                    'AI scoring',
                    '20 practice items',
                    'Lecture Audio Library',
                    'Mini Mock: Speaking (Day 10)',
                ],
                day: 'Days 8–10',
                hasMiniMock: true,
                hasAudioPractice: true,
                hasAIDrills: true,
            },
            {
                id: 7,
                title: 'Answer Short Question',
                topics: [
                    'Rapid recall training',
                    '100 practice questions',
                    'Speed Audio Drills',
                ],
                day: 'Days 9–10',
                hasAudioPractice: true,
            },
        ],
    },
    {
        id: 3,
        title: 'Writing Mastery',
        dayRange: 'Days 11–15',
        icon: '✍️',
        color: '#ffa726',
        modules: [
            {
                id: 8,
                title: 'Summarize Written Text',
                topics: [
                    'One-sentence strategy',
                    'Grammar templates',
                    '20 practice items',
                    'Timed Writing Drills',
                ],
                day: 'Days 11–13',
                hasAIDrills: true,
            },
            {
                id: 9,
                title: 'Write Essay',
                topics: [
                    '4 essay templates',
                    'Structure training',
                    'AI essay scoring',
                    '10 practice essays',
                    'Mini Mock: Writing (Day 15)',
                ],
                day: 'Days 14–15',
                hasMiniMock: true,
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 4,
        title: 'Reading Mastery',
        dayRange: 'Days 16–20',
        icon: '📖',
        color: '#42a5f5',
        modules: [
            {
                id: 10,
                title: 'Reading & Writing Fill in the Blanks',
                topics: [
                    'Collocation training',
                    'Grammar patterns',
                    '30 practice items',
                    'Daily Collocation Quiz',
                ],
                day: 'Days 16–17',
                hasAIDrills: true,
            },
            {
                id: 11,
                title: 'Re-order Paragraphs',
                topics: [
                    'Logic patterns',
                    'Linking words',
                    '20 practice items',
                ],
                day: 'Day 18',
            },
            {
                id: 12,
                title: 'Reading Fill in the Blanks',
                topics: [
                    'Vocabulary builder',
                    '30 practice items',
                ],
                day: 'Day 19',
            },
            {
                id: 13,
                title: 'MCQ (Single & Multiple)',
                topics: [
                    'Elimination technique',
                    'Negative marking strategy',
                    '20 practice items',
                    'Mini Mock: Reading (Day 20)',
                ],
                day: 'Day 20',
                hasMiniMock: true,
            },
        ],
    },
    {
        id: 5,
        title: 'Listening Mastery',
        dayRange: 'Days 21–25',
        icon: '🎧',
        color: '#ab47bc',
        modules: [
            {
                id: 14,
                title: 'Summarize Spoken Text',
                topics: [
                    'Note-taking method',
                    'Structure template',
                    '10 practice audios',
                    'Daily Audio Summaries',
                ],
                day: 'Day 21',
                hasAudioPractice: true,
            },
            {
                id: 15,
                title: 'Listening Fill in the Blanks',
                topics: [
                    'Spelling training',
                    'Accent training',
                    '20 practice items',
                ],
                day: 'Day 22',
                hasAudioPractice: true,
            },
            {
                id: 16,
                title: 'Highlight Correct Summary',
                topics: [
                    'Summary recognition',
                    '10 practice items',
                ],
                day: 'Day 23',
            },
            {
                id: 17,
                title: 'Select Missing Word',
                topics: [
                    'Prediction technique',
                    '10 practice items',
                ],
                day: 'Day 23',
                hasAudioPractice: true,
            },
            {
                id: 18,
                title: 'Highlight Incorrect Words',
                topics: [
                    'Focus training',
                    '20 practice items',
                ],
                day: 'Day 24',
                hasAudioPractice: true,
            },
            {
                id: 19,
                title: 'Write from Dictation',
                topics: [
                    'Dictation drills',
                    'Spelling correction',
                    '50 practice items',
                    'Mini Mock: Listening (Day 25)',
                ],
                day: 'Day 25',
                hasMiniMock: true,
                hasAudioPractice: true,
            },
        ],
    },
    {
        id: 6,
        title: 'Mock Tests & AI Feedback',
        dayRange: 'Days 26–30',
        icon: '🏆',
        color: '#ef5350',
        modules: [
            {
                id: 20,
                title: 'Full Mock Tests',
                topics: [
                    '3 full-length PTE Academic mock tests',
                    'Auto-timed',
                    'Auto-scored',
                    'Speaking AI analysis',
                    'Writing AI scoring',
                    'Listening & reading breakdown',
                ],
                day: 'Days 26–28',
                hasMiniMock: true,
                hasAIDrills: true,
            },
            {
                id: 21,
                title: 'Performance Dashboard',
                topics: [
                    'Score prediction',
                    'Weak area identification',
                    'Improvement tracker',
                    'Study recommendations',
                ],
                day: 'Days 29–30',
                hasAIDrills: true,
            },
        ],
    },
    {
        id: 7,
        title: 'Bonus Tools',
        dayRange: 'Premium Only',
        icon: '💎',
        color: '#26c6da',
        modules: [
            {
                id: 22,
                title: 'Vocabulary Builder',
                topics: [
                    '500 essential PTE words',
                    'Audio pronunciation',
                    'Flashcards',
                    'Mini quizzes',
                ],
                hasAudioPractice: true,
            },
            {
                id: 23,
                title: 'Grammar Booster',
                topics: [
                    '20 grammar micro-lessons',
                    'Error correction practice',
                ],
                hasAIDrills: true,
            },
            {
                id: 24,
                title: 'Speaking Fluency Trainer',
                topics: [
                    'Shadowing',
                    'Accent neutralisation',
                    'Speed control',
                ],
                hasAudioPractice: true,
            },
            {
                id: 25,
                title: 'Writing Templates Library',
                topics: [
                    'Essay templates',
                    'SWT templates',
                    'Connectors list',
                ],
            },
        ],
    },
    {
        id: 8,
        title: 'Student Support',
        dayRange: 'Ongoing',
        icon: '🤝',
        color: '#66bb6a',
        modules: [
            {
                id: 26,
                title: 'WhatsApp Support (Optional Add-On)',
                topics: [
                    'Ask questions',
                    'Get feedback',
                    'Motivation support',
                ],
            },
            {
                id: 27,
                title: 'Live Classes (Optional Add-On)',
                topics: [
                    'Weekly live sessions',
                    'Q&A',
                    'Speaking practice',
                ],
            },
        ],
    },
];

export const PTE_TOTAL_MODULES = 27;
export const PTE_TOTAL_DAYS = 30;
